import { useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router';

import { Avatar } from '@/components/ui/Avatar';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { ROUTES } from '@/constants/routes';
import { accountName, useAuthUser, usePermissions } from '@/features/auth';
import { cn } from '@/utils/cn';

import { STAFF_NAV } from './nav-items';
import styles from './Sidebar.module.css';

export interface SidebarProps {
  /** Only matters below the drawer breakpoint; on wide screens the sidebar is always shown. */
  open: boolean;
  onClose: () => void;
}

/** Logo, the nav groups the user's permissions allow, and the signed-in user at the bottom. */
export function Sidebar({ open, onClose }: SidebarProps) {
  const user = useAuthUser();
  const { can } = usePermissions();
  const navRef = useRef<HTMLElement>(null);
  const name = accountName(user);

  const groups = STAFF_NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.permission || can(item.permission)),
  })).filter((group) => group.items.length > 0);

  // Opening the drawer moves focus into it, so keyboard users land on the nav.
  useEffect(() => {
    if (open) navRef.current?.querySelector('a')?.focus();
  }, [open]);

  return (
    <aside className={cn(styles.sidebar, open && styles.open)} aria-label="Main">
      <div className={styles.header}>
        {/* The badge is drawn on near-black, so it gets a near-black panel of its own. */}
        <Link to={ROUTES.DASHBOARD} className={styles.brand} onClick={onClose}>
          <BrandLogo size="md" caption="GYM · FRONT DESK" onDark />
        </Link>
        <IconButton
          icon="close"
          label="Close menu"
          variant="ghost"
          className={styles.close}
          onClick={onClose}
        />
      </div>

      <nav ref={navRef} className={styles.nav}>
        {groups.map((group) => (
          <div key={group.label} className={styles.group}>
            <p className={styles.groupLabel}>{group.label}</p>
            <ul className={styles.items}>
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => cn(styles.link, isActive && styles.active)}
                    onClick={onClose}
                  >
                    <Icon name={item.icon} size={20} />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={styles.footer}>
        <NavLink
          to={ROUTES.SETTINGS}
          className={({ isActive }) => cn(styles.user, isActive && styles.userActive)}
          aria-label={`Settings — signed in as ${name}`}
          onClick={onClose}
        >
          <Avatar name={name} size="md" />
          <span className={styles.userText}>
            <span className={styles.userName}>{name}</span>
            <span className={styles.userRole}>{user.role.name}</span>
          </span>
          <Icon name="settings" size={18} className={styles.userIcon} />
        </NavLink>
      </div>
    </aside>
  );
}
